/**
 * useMultiDeviceExitDetection Hook
 *
 * Manages exit detection across ALL exit devices (not just one hardcoded device).
 * Automatically filters devices by zone_direction === 'saida'.
 *
 * NOTE: This hook returns device configurations only. The actual detection hooks
 * should be created at the component level to avoid violating Rules of Hooks.
 *
 * Currently supports:
 * - Hikvision facial recognition devices
 *
 * TODO: Add support for:
 * - ControlID devices
 * - Other manufacturers
 */

import { useMemo, useEffect, useState, useCallback } from 'react';
import { VisitorForExitCheck } from './useVisitorExitDetection';
import { logger } from '../services/logger';

/**
 * Device from API (subset of fields we need)
 */
export interface Device {
  id: string | number; // Support both string and number IDs
  name: string;
  zone_name?: string;
  zone_direction?: string;
  ip_address?: string;
  local_ip_address?: string;
  port?: number;
  username: string;
  password: string;
  manufacturer?: string;
  device_type?: string;
  status?: string;
}

/**
 * Processed exit device configuration
 */
export interface ExitDeviceInfo {
  id: string | number; // Support both string and number IDs
  name: string;
  ip: string;
  port: number;
  username: string;
  password: string;
  deviceType: string; // 'hikvision', 'controlid', etc.
  zoneDirection: string; // 'saida'
}

/**
 * Hook return value - just returns device configs
 */
export interface UseMultiDeviceExitDetectionResult {
  exitDevices: ExitDeviceInfo[]; // All exit devices
  supportedDevices: ExitDeviceInfo[]; // Hikvision only
  unsupportedDevices: ExitDeviceInfo[]; // Other manufacturers
}

/**
 * Multi-device exit detection info hook
 *
 * Returns exit device configurations for use with individual exit detection hooks.
 * Does NOT create hook instances dynamically (to avoid Rules of Hooks violations).
 *
 * Usage:
 * ```typescript
 * const { supportedDevices, unsupportedDevices } = useMultiDeviceExitDetection(allDevices);
 *
 * // Then create detection hooks for specific devices at component level
 * // See OperationalView for implementation example
 * ```
 */
export const useMultiDeviceExitDetection = (devices: Device[]): UseMultiDeviceExitDetectionResult => {
  // Filter to exit devices only and transform to ExitDeviceInfo
  const exitDevices = useMemo(() => {
    const filtered = devices.filter(d =>
      d.zone_direction === 'saida' && // Exit zone
      d.status === 'online' &&        // Online only
      (d.device_type === 'facial_recognition' || d.manufacturer === 'hikvision') // Facial recognition devices
    );

    const mapped: ExitDeviceInfo[] = filtered.map(d => {
      // Get raw IP address
      let rawIp = d.ip_address || d.local_ip_address || '';

      // Strip protocol (http:// or https://) if present
      rawIp = rawIp.replace(/^https?:\/\//, '');

      // Strip port if present in IP string (e.g., "192.168.1.1:8080" -> "192.168.1.1")
      const ipWithoutPort = rawIp.split(':')[0];

      return {
        id: d.id,
        name: d.zone_name || d.name || 'Unknown',
        ip: ipWithoutPort, // Clean IP without protocol or port
        port: d.port || 8213, // Default Hikvision port
        username: d.username,
        password: d.password,
        deviceType: (d.manufacturer || 'hikvision').toLowerCase(),
        zoneDirection: d.zone_direction || 'saida',
      };
    });

    logger.info('useMultiDeviceExitDetection', 'Exit devices filtered', {
      totalDevices: devices.length,
      exitDevices: mapped.length,
      devices: mapped.map(d => `${d.name} at ${d.ip}:${d.port} (${d.deviceType})`),
    });

    return mapped;
  }, [devices]);

  // Separate supported and unsupported devices
  const supportedDevices = useMemo(() => {
    return exitDevices.filter(d => d.deviceType === 'hikvision');
  }, [exitDevices]);

  const unsupportedDevices = useMemo(() => {
    const unsupported = exitDevices.filter(d => d.deviceType !== 'hikvision');

    if (unsupported.length > 0) {
      logger.warn('useMultiDeviceExitDetection', 'Unsupported exit devices found', {
        devices: unsupported.map(d => ({
          name: d.name,
          type: d.deviceType,
        })),
      });

      unsupported.forEach(d => {
        console.log(`[TODO] Exit detection for ${d.deviceType} (${d.name}) not implemented yet`);
      });
    }

    return unsupported;
  }, [exitDevices]);

  return {
    exitDevices,
    supportedDevices,
    unsupportedDevices,
  };
};

/**
 * Helper hook to perform manual exit detection scan across devices
 *
 * This avoids dynamic hook creation by providing a manual scan function.
 */
export interface UseManualExitScanOptions {
  devices: ExitDeviceInfo[];
  insideVisitors: VisitorForExitCheck[];
  onVisitorExited: (personId: string, roleId: string) => void;
  scanTimeRange?: number; // Hours (default: 24)
}

export const useManualExitScan = (options: UseManualExitScanOptions) => {
  const { devices, insideVisitors, onVisitorExited, scanTimeRange = 24 } = options;
  const [isScanning, setIsScanning] = useState(false);
  const [lastScanTime, setLastScanTime] = useState<Date | null>(null);

  const performScan = useCallback(async () => {
    if (devices.length === 0 || insideVisitors.length === 0) {
      logger.debug('useManualExitScan', 'Skipping scan - no devices or visitors');
      return;
    }

    setIsScanning(true);

    try {
      logger.info('useManualExitScan', 'Starting manual exit scan', {
        deviceCount: devices.length,
        visitorCount: insideVisitors.length,
        timeRange: `${scanTimeRange}h`,
      });

      // For now, just log that we would scan
      // TODO: Implement actual device scanning without using hooks
      logger.info('useManualExitScan', 'Manual scan would check devices', {
        devices: devices.map(d => d.name),
      });

      setLastScanTime(new Date());
    } catch (error) {
      logger.error('useManualExitScan', 'Manual scan failed', error);
    } finally {
      setIsScanning(false);
    }
  }, [devices, insideVisitors, onVisitorExited, scanTimeRange]);

  return {
    performScan,
    isScanning,
    lastScanTime,
  };
};
